/**
 * Free Quote — single address field, Google Places (New).
 *
 * The v2 form asks for the service address once, in one box, and derives the
 * structured parts from whatever the customer picks. It replaces the four-field
 * block (apartment / street / suburb / state+postcode) that used to sit in its
 * own panel: the same information, one question.
 *
 * MARKUP CONTRACT — the template owns the ids, this file only reads them:
 *
 *   #fq2-address-wrap        positioned wrapper; the dropdown is absolute inside it
 *     data-address-input     id of the visible input          (default `address`)
 *     data-address-line1     id of the street hidden input    (default `address_line1`)
 *     data-address-city      id of the suburb hidden input    (default `city`)
 *     data-address-state     id of the state hidden input     (default `state`)
 *     data-address-postcode  id of the postcode hidden input  (default `zip_code`)
 *   #fq2-address-dropdown    empty listbox this file fills
 *
 * WHAT GOES WHERE. The visible field holds the whole address as Google formats
 * it, because that is what the customer just read and agreed to. The hidden
 * fields carry the parts the backend has columns for — `address_line1` is the
 * street line alone (unit, number, road), not a copy of the full string, so the
 * suburb is not stored twice.
 *
 * A TYPED ADDRESS IS STILL AN ADDRESS. Picking a suggestion is not required:
 * the field is a plain text input that happens to offer help, and anything typed
 * is posted as `address_line1`. What a typed edit does do is clear the suburb,
 * state and postcode captured from the last pick — leaving them behind would
 * post "Bondi NSW 2026" under an address in Perth. This is also why the form
 * does not force a pick: an address our data does not know is a lead, not an
 * error.
 *
 * BILLING. Autocomplete is charged per session, and a session ends when place
 * details are fetched — so one token is created per pick and replaced right
 * after, and requests are debounced. Nothing is fetched until the second
 * character.
 *
 * Requires "Places API (New)" on the Google Cloud project. Loaded by
 * pages/quote/free_quote_v2.html, which calls initFreeQuoteAddressField as the
 * Maps loader callback; without the key the loader is never rendered and this
 * file leaves a working plain input behind.
 */

(function () {
    'use strict';

    var DEBOUNCE_MS = 220;

    // Australia only. The New API accepts at most five primary types, and these
    // are the five that describe a place someone can be cleaned at: a street
    // address, a named building, a unit within one, plus the two coarser
    // answers (suburb, postcode) for a customer who does not want to give a
    // street until they have a price.
    var INCLUDED_TYPES = ['street_address', 'premise', 'subpremise', 'locality', 'postal_code'];

    var COMPONENT_TYPES = {
        subpremise: ['subpremise'],
        streetNumber: ['street_number'],
        route: ['route'],
        suburb: ['locality', 'postal_town', 'sublocality_level_1', 'sublocality'],
        state: ['administrative_area_level_1'],
        postalCode: ['postal_code'],
    };

    function findComponent(components, types) {
        if (!Array.isArray(components)) return null;
        for (var i = 0; i < types.length; i++) {
            for (var j = 0; j < components.length; j++) {
                var c = components[j];
                if (c && Array.isArray(c.types) && c.types.indexOf(types[i]) !== -1) return c;
            }
        }
        return null;
    }

    function componentText(components, types) {
        var found = findComponent(components, types);
        return (found && (found.longText || found.shortText)) || '';
    }

    /** Set a value and tell the rest of the form about it (draft, summary, validation). */
    function setValue(el, value) {
        if (!el) return;
        var next = value == null ? '' : String(value);
        if (el.value === next) return;
        el.value = next;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * "12/34 Smith Street" — the street line on its own.
     *
     * Falls back to the leading part of the formatted address when Google gives
     * components we cannot assemble (a named building, say), and to the raw text
     * when there is nothing else.
     */
    function streetLine(components, formatted) {
        var unit = componentText(components, COMPONENT_TYPES.subpremise);
        var number = componentText(components, COMPONENT_TYPES.streetNumber);
        var route = componentText(components, COMPONENT_TYPES.route);

        var street = [number, route].filter(Boolean).join(' ');
        if (street && unit) street = unit + '/' + street;
        if (street) return street;

        return String(formatted || '').split(',')[0].trim();
    }

    /** Google ends every AU address with ", Australia"; the customer knows. */
    function trimCountry(formatted) {
        return String(formatted || '').replace(/,\s*Australia\s*$/i, '').trim();
    }

    function elements() {
        var wrap = document.getElementById('fq2-address-wrap');
        if (!wrap) return null;

        function byId(attr, fallback) {
            return document.getElementById(wrap.getAttribute(attr) || fallback);
        }

        var input = byId('data-address-input', 'address');
        if (!input) return null;

        return {
            wrap: wrap,
            input: input,
            dropdown: document.getElementById('fq2-address-dropdown'),
            line1: byId('data-address-line1', 'address_line1'),
            city: byId('data-address-city', 'city'),
            state: byId('data-address-state', 'state'),
            zip: byId('data-address-postcode', 'zip_code'),
        };
    }

    /**
     * Keep the hidden fields honest without Places.
     *
     * Runs whether or not the Maps script ever loads: whatever is typed is the
     * street line, and a typed change drops the suburb / state / postcode of a
     * pick it no longer describes. `data-fq-place` marks the text a pick
     * produced, so an edit is anything that no longer matches it.
     */
    function wirePlainInput(el) {
        el.input.addEventListener('input', function () {
            var typed = el.input.value.trim();
            if (typed === (el.input.dataset.fqPlace || '')) return;

            delete el.input.dataset.fqPlace;
            setValue(el.line1, typed);
            setValue(el.city, '');
            setValue(el.state, '');
            setValue(el.zip, '');
        });
    }

    function init(places) {
        var el = elements();
        if (!el) return;

        // Normally already wired by the ready() block below; this covers a Maps
        // callback that beats DOMContentLoaded.
        if (!el.input.dataset.fqPlainWired) {
            el.input.dataset.fqPlainWired = '1';
            wirePlainInput(el);
        }

        if (!el.dropdown || !places || !places.AutocompleteSuggestion) return;

        var AutocompleteSuggestion = places.AutocompleteSuggestion;
        var AutocompleteSessionToken = places.AutocompleteSessionToken;

        var token = new AutocompleteSessionToken();
        var suggestions = [];
        var activeIndex = -1;
        var debounceId = null;
        var lastQuery = '';

        function closeList() {
            el.dropdown.classList.add('hidden');
            el.dropdown.innerHTML = '';
            suggestions = [];
            activeIndex = -1;
            el.input.setAttribute('aria-expanded', 'false');
        }

        function highlight(idx) {
            activeIndex = idx;
            Array.prototype.forEach.call(el.dropdown.children, function (row, i) {
                row.classList.toggle('bg-[#ecfce8]', i === idx);
                row.setAttribute('aria-selected', i === idx ? 'true' : 'false');
            });
        }

        function renderRows() {
            el.dropdown.innerHTML = '';
            if (!suggestions.length) { closeList(); return; }

            suggestions.forEach(function (pred, i) {
                var row = document.createElement('div');
                row.setAttribute('role', 'option');
                row.className = 'px-3 py-2.5 text-sm cursor-pointer hover:bg-[#ecfce8] ' +
                    (i < suggestions.length - 1 ? 'border-b border-gray-100' : '');
                row.textContent = (pred.text && pred.text.text) || '';
                // mousedown, not click: it has to beat the input's own blur.
                row.addEventListener('mousedown', function (e) {
                    e.preventDefault();
                    choose(i);
                });
                el.dropdown.appendChild(row);
            });

            el.dropdown.classList.remove('hidden');
            el.input.setAttribute('aria-expanded', 'true');
            highlight(-1);
        }

        function fetchSuggestions(input) {
            AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: input,
                sessionToken: token,
                includedRegionCodes: ['au'],
                includedPrimaryTypes: INCLUDED_TYPES,
                language: 'en-AU',
                region: 'au',
            })
                .then(function (res) {
                    suggestions = ((res && res.suggestions) || [])
                        .map(function (s) { return s.placePrediction; })
                        .filter(Boolean);
                    renderRows();
                })
                .catch(function () { closeList(); });
        }

        function choose(idx) {
            var pred = suggestions[idx];
            if (!pred) return;

            var place = pred.toPlace();
            place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'displayName'] })
                .then(function () {
                    var comps = place.addressComponents || [];
                    var formatted = trimCountry(place.formattedAddress)
                        || (pred.text && pred.text.text)
                        || el.input.value;

                    setValue(el.input, formatted);
                    // Remember what the pick wrote, so wirePlainInput() can tell
                    // a later edit from this very assignment.
                    el.input.dataset.fqPlace = formatted;

                    setValue(el.line1, streetLine(comps, formatted));
                    setValue(el.city, componentText(comps, COMPONENT_TYPES.suburb));
                    setValue(el.zip, componentText(comps, COMPONENT_TYPES.postalCode));

                    var stateComp = findComponent(comps, COMPONENT_TYPES.state);
                    setValue(el.state, (stateComp && (stateComp.shortText || stateComp.longText)) || '');
                })
                .catch(function () { /* keep whatever they typed */ })
                .then(function () {
                    // A details fetch closes the billing session — start the next one.
                    token = new AutocompleteSessionToken();
                    closeList();
                });
        }

        el.input.addEventListener('input', function () {
            var q = el.input.value.trim();
            lastQuery = q;
            if (debounceId) clearTimeout(debounceId);
            if (q.length < 2) { closeList(); return; }
            debounceId = setTimeout(function () {
                if (el.input.value.trim() === lastQuery) fetchSuggestions(lastQuery);
            }, DEBOUNCE_MS);
        });

        el.input.addEventListener('keydown', function (e) {
            if (el.dropdown.classList.contains('hidden') || !suggestions.length) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                highlight((activeIndex + 1) % suggestions.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                highlight((activeIndex - 1 + suggestions.length) % suggestions.length);
            } else if (e.key === 'Enter') {
                if (activeIndex >= 0) { e.preventDefault(); choose(activeIndex); }
            } else if (e.key === 'Escape') {
                closeList();
            }
        });

        document.addEventListener('click', function (e) {
            if (!el.dropdown.contains(e.target) && e.target !== el.input) closeList();
        });
        el.input.addEventListener('blur', function () {
            // Delayed so a suggestion's mousedown lands first.
            setTimeout(closeList, 150);
        });
    }

    function ready(fn) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
        } else {
            fn();
        }
    }

    // The plain-input wiring must exist even when Maps never loads (no key, a
    // blocked script, an offline visitor), so it runs on its own.
    ready(function () {
        var el = elements();
        if (el && !el.input.dataset.fqPlainWired) {
            el.input.dataset.fqPlainWired = '1';
            wirePlainInput(el);
        }
    });

    // Maps loader callback (see pages/quote/free_quote_v2.html).
    window.initFreeQuoteAddressField = function () {
        ready(function () {
            if (!window.google || !google.maps || !google.maps.importLibrary) return;
            google.maps.importLibrary('places')
                .then(function (places) { init(places); })
                .catch(function () { /* Places unavailable — the plain input stands */ });
        });
    };
})();
